/**
 * Containment policy — whether the booking flow is running contained and, if
 * so, what that costs the visitor: which routes stay reachable, whether the
 * booking-flow redesign flag gate is bypassed, whether marketplace
 * interruptions may surface, and whether the legacy (non-Bokadirekt Pay) online
 * payment methods are on offer.
 *
 * The decisions are a pure function of {@link ContainmentContext}: the runtime
 * facts are fields there and a {@link ContainmentMode} decides all four rules,
 * so consumers read {@link getContainmentPolicy} and never learn which surface
 * they are on. Two surfaces contain the flow: the widget iframe (`'embed'`),
 * and the embed bundle running as the top-level document of a tab, opened from
 * a Themed Booking Link (`'linked'`, a Linked Session). The pay and login
 * popups a framed session opens are satellites of embed mode with no mode of
 * their own: nothing in a popup consults the policy, and a popup closes rather
 * than exits. Rules that hinge on the embed runtime rather than on containment
 * — the popups suppressing interruptions, popup login, host messaging — stay in
 * `helpers/embed` and `helpers/embedPolicy`.
 */

import { EmbedContext, getEmbedContext, isEmbedBundle } from './embed';
import { allowsRoute as embedAllowsRoute } from './embedPolicy';

/** The surface containing the flow; `null` when the marketplace runs free. */
export type ContainmentMode = 'embed' | 'linked' | null;

/** Runtime facts the policy is decided from. */
export type ContainmentContext = {
  /** The runtime this document is in: framed, one of the popup satellites, or top-level. */
  runtime: EmbedContext;
  /** The running script is the booking-widget SPA (the `embed` entry), not the marketplace SPA. */
  embedBundle: boolean;
};

export type ContainmentPolicy = {
  /** Whether a visitor may reach `path`; anything else renders the blocked screen. */
  allowsRoute: (path: string) => boolean;
  /** Whether booking-flow step pages skip the redesign Confidence-flag gate. */
  bypassesRedesignFlag: boolean;
  /** Whether promos, NPS, app install and last-minute modals may surface. */
  allowsInterruptions: boolean;
  /**
   * Whether the legacy online payment methods (Klarna, Qliro, Swish, card on
   * file outside Bokadirekt Pay) cannot be completed here. The embed SPA has no
   * return routes for them and the pay popup cannot run them, so a staff member
   * without Bokadirekt Pay is booked with pay-at-place only — even where online
   * payment is otherwise required. The forced case (deals, Sista minuten) is
   * exempt.
   */
  legacyPaymentsUnavailable: boolean;
};

export function resolveContainmentMode(context: ContainmentContext): ContainmentMode {
  if (context.runtime === 'framed') return 'embed';
  if (context.runtime === 'standalone' && context.embedBundle) return 'linked';
  return null;
}

export function resolveContainmentPolicy(context: ContainmentContext): ContainmentPolicy {
  const mode = resolveContainmentMode(context);
  switch (mode) {
    case 'embed':
      return {
        allowsRoute: embedAllowsRoute,
        bypassesRedesignFlag: true,
        allowsInterruptions: false,
        legacyPaymentsUnavailable: true,
      };
    case 'linked':
      // Containment is structural: the embed SPA holds no marketplace routes,
      // so there is nothing to allow-list.
      return {
        allowsRoute: () => true,
        bypassesRedesignFlag: true,
        allowsInterruptions: false,
        legacyPaymentsUnavailable: true,
      };
    case null:
      return {
        allowsRoute: () => true,
        bypassesRedesignFlag: false,
        allowsInterruptions: true,
        legacyPaymentsUnavailable: false,
      };
    default: {
      const unhandled: never = mode;
      return unhandled;
    }
  }
}

/** The policy for the context this window is running in. */
export function getContainmentPolicy(): ContainmentPolicy {
  return resolveContainmentPolicy(currentContext());
}

/** The mode this window is running in — for the rules that differ between the two contained surfaces. */
export function getContainmentMode(): ContainmentMode {
  return resolveContainmentMode(currentContext());
}

function currentContext(): ContainmentContext {
  return { runtime: getEmbedContext(), embedBundle: isEmbedBundle() };
}
